import {
  Box,
  type BoxProps,
  Container,
  Heading,
  HStack,
  Icon,
  SimpleGrid,
  Text,
  VStack
} from "@chakra-ui/react";
import { Brain, ChevronRight, CircleDot, Crown, Gamepad2, Heart, Play, Users } from "lucide-react";
import { type ReactNode } from "react";

import { siteFeaturedGame } from "@/data/games";
import { siteConfig } from "@/data/site";
import { nvpTokens } from "@/design-system/tokens";
import { FeaturedGame } from "@/features/games/FeaturedGame";
import { GameGrid } from "@/features/games/GameGrid";
import { useI18n } from "@/i18n";
import { MotionBox } from "@/shared/animations/MotionBox";
import { fadeUp, spring, stagger } from "@/shared/animations/motion";
import { PageShell } from "@/shared/layout/PageShell";
import { SeoHead } from "@/shared/seo/SeoHead";
import { GlowButton } from "@/shared/ui/GlowButton";
import { NvpCard } from "@/shared/ui/NvpCard";
import { SectionHeading } from "@/shared/ui/SectionHeading";

const valueIcons = [Brain, Users, Crown, Heart] as const;
const heroColors = {
  white: "#ffffff",
  pink: "#ff44df",
  yellow: "#ffd42c"
} as const;

const revealFadeUp = {
  hidden: fadeUp.hidden,
  show: {
    ...fadeUp.show,
    transition: spring
  }
} as const;

export function LandingPage() {
  const { copy } = useI18n();

  return (
    <PageShell>
      <SeoHead />
      <Box as="main" position="relative" overflow="hidden" bg="nvp.ink">
        <Box
          as="section"
          position="relative"
          overflow="hidden"
          bg="nvp.ink"
          minH={{
            base: `max(${nvpTokens.landing.hero.viewportMinHeight}, ${nvpTokens.landing.hero.minHeight.base})`,
            md: `max(${nvpTokens.landing.hero.viewportMinHeight}, ${nvpTokens.landing.hero.backgroundFitWidthHeight})`
          }}
          h={{
            base: `max(${nvpTokens.landing.hero.viewportMinHeight}, ${nvpTokens.landing.hero.minHeight.base})`,
            md: `max(${nvpTokens.landing.hero.viewportMinHeight}, ${nvpTokens.landing.hero.backgroundFitWidthHeight})`
          }}
        >
          <Box
            aria-hidden="true"
            position="absolute"
            inset={0}
            display="flex"
            alignItems={{ base: "stretch", md: "center" }}
            overflow="hidden"
            zIndex={0}
            pointerEvents="none"
          >
            <Box
              as="picture"
              w="100%"
              h={{ base: "100%", md: "100%", xl: "auto" }}
              sx={{
                WebkitMaskImage: nvpTokens.landing.hero.backgroundFadeMask,
                maskImage: nvpTokens.landing.hero.backgroundFadeMask
              }}
            >
              <source
                media="(max-width: 767px)"
                srcSet={siteConfig.assets.firstSectionBackgroundIphone}
              />
              <source
                media="(max-width: 1199px)"
                srcSet={siteConfig.assets.firstSectionBackgroundIpad}
              />
              <Box
                as="img"
                src={siteConfig.assets.firstSectionBackground}
                alt=""
                w="100%"
                h={{ base: "100%", md: "100%", xl: "auto" }}
                objectFit={{ base: "cover", md: "cover", xl: "fill" }}
                objectPosition="center"
                display="block"
                draggable={false}
                loading="eager"
                decoding="async"
              />
            </Box>
          </Box>
          <Box
            aria-hidden="true"
            position="absolute"
            inset={0}
            zIndex={1}
            pointerEvents="none"
            bg={nvpTokens.landing.hero.scrim}
          />
          <Container
            maxW={nvpTokens.landing.hero.container}
            position="relative"
            zIndex={2}
            h={{ md: "100%" }}
            pt={nvpTokens.landing.hero.paddingTop}
            pb={nvpTokens.landing.hero.paddingBottom}
          >
            <MotionBox
              variants={stagger}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.36 }}
              h="100%"
            >
              <VStack
                align="start"
                justify="center"
                spacing={0}
                h={{ md: "100%" }}
                minH={{ base: nvpTokens.landing.hero.minHeight.base, md: "auto" }}
                maxW={nvpTokens.landing.hero.contentMaxWidth}
                px={nvpTokens.landing.hero.contentPaddingX}
              >
                <Box
                  w={{ base: "fit-content", md: "auto" }}
                  maxW="100%"
                  p={{ base: "16px", md: 0 }}
                  mx={{ base: "-16px", md: 0 }}
                  borderRadius={{ base: "8px", md: 0 }}
                  bg={{ base: nvpTokens.landing.hero.textPanel.background, md: "transparent" }}
                  borderWidth={{ base: "1px", md: 0 }}
                  borderColor={nvpTokens.landing.hero.textPanel.border}
                  boxShadow={{ base: nvpTokens.landing.hero.textPanel.shadow, md: "none" }}
                  backdropFilter={{
                    base: nvpTokens.landing.hero.textPanel.backdropFilter,
                    md: "none"
                  }}
                >
                  <MotionBox variants={revealFadeUp}>
                    <Text
                      color="nvp.cyan"
                      fontSize={{ base: "sm", md: "md" }}
                      fontWeight={900}
                      textTransform="uppercase"
                      textShadow="0 0 14px rgba(55,213,255,0.74)"
                    >
                      {copy.hero.eyebrow}
                    </Text>
                  </MotionBox>
                  <MotionBox variants={revealFadeUp} mt={nvpTokens.spacing.md}>
                    <Heading
                      as="h1"
                      fontFamily="'Anton', Impact, 'Arial Narrow Bold', Inter, sans-serif"
                      fontSize={nvpTokens.landing.hero.titleFontSize}
                      lineHeight={nvpTokens.landing.hero.titleLineHeight}
                      fontWeight={400}
                      textTransform="uppercase"
                    >
                      <Box as="span" display="block" color={heroColors.white}>
                        {copy.hero.titleLineOne}
                      </Box>
                      <Box as="span" display="block" color={heroColors.pink}>
                        {copy.hero.titleLineTwo}
                      </Box>
                      <Box as="span" display="block" color={heroColors.yellow}>
                        {copy.hero.titleLineThree}
                      </Box>
                    </Heading>
                  </MotionBox>
                  <MotionBox variants={revealFadeUp} mt={nvpTokens.spacing.lg}>
                    <Text
                      color="whiteAlpha.900"
                      maxW={nvpTokens.landing.hero.contentMaxWidth}
                      fontSize={{ base: "md", md: "lg" }}
                      lineHeight={1.55}
                      whiteSpace={{ base: "normal", md: "pre-line" }}
                      textShadow="0 0 20px rgba(0,0,0,0.7)"
                    >
                      {copy.hero.subtitle}
                    </Text>
                  </MotionBox>
                </Box>
                <MotionBox
                  variants={revealFadeUp}
                  mt={{ base: nvpTokens.spacing.xl, md: nvpTokens.spacing["2xl"] }}
                >
                  <HStack flexWrap="wrap" gap={3}>
                    <GlowButton as="a" href="#games" leftIcon={<Gamepad2 size={18} />}>
                      {copy.hero.viewGames}
                    </GlowButton>
                    <GlowButton
                      as="a"
                      href={`/games/${siteFeaturedGame.slug}`}
                      glow="blue"
                      variantKind="secondary"
                      leftIcon={<Play size={18} />}
                    >
                      {copy.hero.playNow}
                    </GlowButton>
                  </HStack>
                </MotionBox>
              </VStack>
            </MotionBox>
          </Container>
        </Box>

        <Box
          position="relative"
          overflow="hidden"
          pb={{ base: nvpTokens.spacing.xl, md: nvpTokens.spacing["2xl"] }}
          bg="nvp.ink"
        >
          <Container maxW={nvpTokens.layout.container} position="relative">
            <RevealOnScroll mt={{ base: nvpTokens.spacing["2xl"], lg: nvpTokens.spacing["3xl"] }}>
              <FeaturedGame />
            </RevealOnScroll>
          </Container>
        </Box>

        <Box position="relative" overflow="hidden">
          <Container maxW={nvpTokens.layout.container} position="relative">
            <RevealOnScroll>
              <GameGrid />
            </RevealOnScroll>
            <RevealOnScroll>
              <StudioValues />
            </RevealOnScroll>
            <RevealOnScroll>
              <FinalCta />
            </RevealOnScroll>
          </Container>
        </Box>
      </Box>
    </PageShell>
  );
}

function RevealOnScroll({ children, ...props }: BoxProps & { readonly children: ReactNode }) {
  return <Box {...props}>{children}</Box>;
}

function StudioValues() {
  const { copy } = useI18n();

  return (
    <Box id="studio" as="section" py={{ base: 10, md: 14 }}>
      <SectionHeading
        eyebrow={copy.studio.eyebrow}
        title={copy.studio.title}
        body={copy.studio.body}
      />
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={5} mt={8}>
        {copy.studio.values.map((value, index) => {
          const ValueIcon = valueIcons[index] ?? CircleDot;
          return (
            <NvpCard
              key={value.title}
              p={5}
              tone={index % 2 === 0 ? "blue" : "pink"}
              interactive
              minH="150px"
            >
              <HStack align="start" gap={4}>
                <Box
                  display="grid"
                  placeItems="center"
                  h="46px"
                  w="46px"
                  flex="0 0 auto"
                  borderRadius="8px"
                  bg="whiteAlpha.100"
                  color={index % 2 === 0 ? "nvp.cyan" : "nvp.pink"}
                >
                  <Icon as={ValueIcon} boxSize={6} />
                </Box>
                <Box>
                  <Heading as="h3" fontSize="md" mb={2} textTransform="uppercase">
                    {value.title}
                  </Heading>
                  <Text color="whiteAlpha.700" fontSize="sm">
                    {value.body}
                  </Text>
                </Box>
              </HStack>
            </NvpCard>
          );
        })}
      </SimpleGrid>
    </Box>
  );
}

function FinalCta() {
  const { copy } = useI18n();

  return (
    <NvpCard
      as="section"
      my={{ base: 10, md: 16 }}
      p={{ base: 6, md: 9 }}
      borderColor="rgba(255, 79, 216, 0.72)"
      bg="radial-gradient(circle at 78% 52%, rgba(255,216,77,0.18), transparent 24%), radial-gradient(circle at 56% 30%, rgba(255,79,216,0.24), transparent 32%), linear-gradient(135deg, rgba(55,213,255,0.16), rgba(255,79,216,0.22), rgba(255,216,77,0.12))"
      tone="yellow"
      showGlow
    >
      <HStack
        position="relative"
        justify="space-between"
        align={{ base: "start", md: "center" }}
        flexDir={{ base: "column", md: "row" }}
        gap={6}
      >
        <Box>
          <Heading
            as="h2"
            fontSize={{ base: "2.2rem", md: "3.2rem" }}
            textTransform="uppercase"
          >
            {copy.cta.title}
          </Heading>
          <Text color="whiteAlpha.800" maxW="520px" mt={3} fontSize="md">
            {copy.cta.body}
          </Text>
        </Box>
        <GlowButton
          as="a"
          href={`/games/${siteFeaturedGame.slug}`}
          glow="yellow"
          minW={{ base: "100%", sm: "240px" }}
          rightIcon={<ChevronRight size={20} />}
        >
          {copy.cta.button}
        </GlowButton>
      </HStack>
    </NvpCard>
  );
}
